/*
 **	Device Detail Panel Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React, { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useUnitPreference } from '../../contexts/UnitPreferenceContext'
import { formatPressureValue } from '../../utils/unitConversion'
import { getDeviceData, getAllLocations, sendAckCommand, sendEscalateCommand, getAlarms, resolveEscalation, createAlarm, checkEscalationPolicyExists, type Device, type DeviceData, type Location, type AlarmResolution } from '../../services/api'
import { OCAColors, Typography, DeviceDetailTypography, Spacing } from '../../theme/colors'
import { IOSSlideUpPanel } from './IOSSlideUpPanel'
import { IOSListSection } from './IOSListSection'
import { MaterialIcon } from './MaterialIcon'
import { StackedChevrons } from './StackedChevrons'
import { PressureSparkline } from './PressureSparkline'
import { DeviceSettingsPanel } from './DeviceSettingsPanel'
import { AlarmResolveModal } from '../AlarmResolveModal'

// Zone Alarm States (matching device list logic)
const enum ZoneAlarmState {
  NOT_ACTIVE = 0,
  NORMAL = 1,
  WARN = 2,
  ALARM = 3,
  ALARM_SILENCED = 4,
  ALARM_ACKNOWLEDGED = 5,
  NORMAL_PAST_UNACK_ALARM = 6,
  WARN_PAST_UNACK_ALARM = 7
}

// Helper functions for alarm state logic (matching device list)
const isAlarmActive = (state: number | null | undefined): boolean => {
  if (state === null || state === undefined) return false

  switch (state) {
    case ZoneAlarmState.ALARM:
    case ZoneAlarmState.ALARM_SILENCED:
    case ZoneAlarmState.ALARM_ACKNOWLEDGED:
      return true
    default:
      return false
  }
}

const isWarningState = (state: number | null | undefined): boolean => {
  if (state === null || state === undefined) return false

  switch (state) {
    case ZoneAlarmState.WARN:
    case ZoneAlarmState.WARN_PAST_UNACK_ALARM:
      return true
    default:
      return false
  }
}

// Custom IOSListItem for device details with smaller text
const DeviceDetailListItem: React.FC<{
  icon?: React.ReactNode
  title: string
  subtitle?: string
  value?: string | React.ReactNode
  status?: string
  statusColor?: string
  onClick?: () => void
  showChevron?: boolean
}> = ({ icon, title, subtitle, value, status, statusColor, onClick, showChevron = true }) => {
  const isClickable = !!onClick

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: `${Spacing.md} ${Spacing.lg}`,
        backgroundColor: OCAColors.background,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        cursor: isClickable ? 'pointer' : 'default',
        minHeight: '60px',
        fontFamily: DeviceDetailTypography.fontFamily,
      }}
    >
      {/* Icon */}
      {icon && (
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: Spacing.md,
          flexShrink: 0,
        }}>
          {icon}
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        <div style={{
          fontSize: DeviceDetailTypography.fontSize.base,
          fontWeight: DeviceDetailTypography.fontWeight.normal,
          color: OCAColors.label,
          lineHeight: '18px',
          marginBottom: subtitle ? '2px' : 0,
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: DeviceDetailTypography.fontSize.sm,
            color: OCAColors.secondaryLabel,
            lineHeight: '16px',
          }}>
            {subtitle}
          </div>
        )}
      </div>

      {/* Value and Status */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        marginLeft: Spacing.sm,
        flexShrink: 0,
        maxWidth: '50%',
      }}>
        {value && (
          <div style={{
            fontSize: DeviceDetailTypography.fontSize.base,
            fontWeight: DeviceDetailTypography.fontWeight.medium,
            color: OCAColors.label,
            lineHeight: '18px',
            marginBottom: status ? '2px' : 0,
            textAlign: 'right',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}>
            {value}
          </div>
        )}
        {status && (
          <div style={{
            fontSize: DeviceDetailTypography.fontSize.sm,
            color: statusColor || OCAColors.secondaryLabel,
            lineHeight: '16px',
            textAlign: 'right',
          }}>
            {status}
          </div>
        )}
      </div>

      {/* Chevron */}
      {showChevron && isClickable && (
        <div style={{
          marginLeft: Spacing.xs,
          color: OCAColors.tertiaryLabel,
          fontSize: '16px',
          fontWeight: DeviceDetailTypography.fontWeight.medium,
        }}>
          ›
        </div>
      )}
    </div>
  )
}

interface PressureTrend {
  dp0?: { direction: 'up' | 'down'; count: number }
  dp1?: { direction: 'up' | 'down'; count: number }
  timestamp: number
}

interface DeviceDetailPanelProps {
  device: Device | null
  isOpen: boolean
  onClose: () => void
  realtimeData?: any // Real-time data from WebSocket
  pressureTrend?: PressureTrend
}

export const DeviceDetailPanel: React.FC<DeviceDetailPanelProps> = ({
  device,
  isOpen,
  onClose,
  realtimeData,
  pressureTrend,
}) => {
  const { getAccessToken, isAuthenticated, user } = useAuth()
  const { pressureUnit } = useUnitPreference()
  const [deviceData, setDeviceData] = useState<DeviceData[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [hasEscalationPolicy, setHasEscalationPolicy] = useState(false)

  useEffect(() => {
    if (isOpen && device && isAuthenticated) {
      loadDeviceData()
    }
  }, [isOpen, device, isAuthenticated])

  const loadDeviceData = async () => {
    if (!device || !isAuthenticated) return

    try {
      setIsLoading(true)
      setError('')

      const token = await getAccessToken()
      if (!token) {
        setError('Unable to get access token')
        return
      }

      // Get namespace ID from user context
      const namespaceId = user?.namespace?.id

      // Load device data, locations, and check escalation policy in parallel
      const [data, locationsData, policyExists] = await Promise.all([
        getDeviceData(device.deviceId, token, undefined, true),
        getAllLocations(token),
        namespaceId ? checkEscalationPolicyExists(token, namespaceId, device.deviceId, device.locationId) : Promise.resolve(false)
      ])

      setDeviceData(data)
      setLocations(locationsData)
      setHasEscalationPolicy(policyExists)

      console.log(`📋 Escalation policy check for device ${device.deviceId}: ${policyExists ? 'EXISTS' : 'NOT FOUND'}`)
    } catch (err) {
      setError('Failed to load device data')
      console.error('Error loading device data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  if (!device) return null

  // Prioritize real-time WebSocket data over API data
  const latestData = realtimeData || (deviceData.length > 0 ? deviceData[0] : null)
  const deviceName = device.name || `Device ${device.deviceId.slice(-5)}`

  // Find location name with proper fallbacks
  const location = locations.find(loc => loc.locationId === device.locationId)
  const locationName =
    (location?.name && location.name.trim() !== '') ? location.name :
    (location?.description && location.description.trim() !== '') ? location.description :
    (location?.address && location.address.trim() !== '') ? location.address :
    device.locationId

  const getStatusInfo = () => {
    if (!latestData) return { status: 'No Data', color: OCAColors.deviceOffline }

    const dp0State = latestData.dp0_alarm_state
    const dp1State = latestData.dp1_alarm_state

    // Check for active alarms (red) - same logic as device list
    if (isAlarmActive(dp0State) || isAlarmActive(dp1State)) {
      return { status: 'Alarm', color: OCAColors.deviceAlarm }
    }

    // Check for warnings (amber) - same logic as device list
    if (isWarningState(dp0State) || isWarningState(dp1State)) {
      return { status: 'Warning', color: OCAColors.deviceWarning }
    }

    // Check last seen time for offline detection - same logic as device list
    if (device.lastSeen) {
      const lastSeenDate = new Date(device.lastSeen)
      const now = new Date()
      const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60)
      
      if (diffMinutes > 60) { // More than 1 hour
        return { status: 'Offline', color: OCAColors.deviceOffline }
      }
    }

    // All normal - green (same logic as device list)
    return { status: 'Online', color: OCAColors.deviceOnline }
  }

  const statusInfo = getStatusInfo()

  const handleResolveSubmit = async (resolution: AlarmResolution) => {
    if (!device || !latestData) return

    setIsResolving(true)
    try {
      const token = await getAccessToken()
      if (!token) {
        throw new Error('Unable to get access token')
      }

      // Get namespace ID from user context
      const namespaceId = user?.namespace?.id
      if (!namespaceId) {
        throw new Error('No namespace ID available - required for multi-tenant design')
      }

      // Query for active alarms for this device
      console.log(`🔍 Looking for active alarm for device ${device.deviceId}`)
      const activeAlarms = await getAlarms(token, 'active', namespaceId)
      let deviceAlarm = activeAlarms.find(alarm => alarm.deviceId === device.deviceId)

      // If no alarm exists yet, create one as a fallback (for alarms that existed before backend started)
      if (!deviceAlarm) {
        console.log(`⚠️ No alarm record found for device ${device.deviceId}`)
        console.log(`Device has alarm states: dp0=${latestData.dp0_alarm_state}, dp1=${latestData.dp1_alarm_state}`)

        // Determine which zone has the alarm
        const dp0Active = isAlarmActive(latestData.dp0_alarm_state)
        const dp1Active = isAlarmActive(latestData.dp1_alarm_state)

        if (dp0Active || dp1Active) {
          console.log(`🔧 Creating alarm record for device ${device.deviceId}`)
          const alarmType = dp0Active ? 'dp0_alarm' : 'dp1_alarm'
          const deviceName = device.name || `Device ${device.deviceId.slice(-5)}`

          const createdAlarmId = await createAlarm(
            token,
            namespaceId,
            device.deviceId,
            alarmType,
            latestData,
            deviceName
          )

          if (createdAlarmId) {
            console.log(`✅ Created alarm record with ID: ${createdAlarmId}`)
            deviceAlarm = { id: createdAlarmId, deviceId: device.deviceId } as any
          } else {
            throw new Error('Failed to create alarm record')
          }
        } else {
          setError('No active alarm state detected on device')
          throw new Error('No active alarm state on device')
        }
      }

      if (!deviceAlarm) {
        throw new Error('Alarm not found and could not be created')
      }

      const alarmId = deviceAlarm.id
      console.log(`✅ Using alarm ID: ${alarmId} for device ${device.deviceId}`)

      const success = await resolveEscalation(alarmId, resolution, token)

      if (success) {
        console.log('✅ Alarm resolved successfully')
        setResolveModalOpen(false)
        // Reload device data to reflect updated alarm state
        await loadDeviceData()
      } else {
        throw new Error('Failed to resolve alarm')
      }
    } catch (err) {
      console.error('❌ Error resolving alarm:', err)
      setError(err instanceof Error ? err.message : 'Failed to resolve alarm')
      throw err // Re-throw so modal can handle error
    } finally {
      setIsResolving(false)
    }
  }

  return (
    <>
      <IOSSlideUpPanel
        isOpen={isOpen}
        onClose={onClose}
        title={deviceName}
        height="85vh"
        headerAction={
          <button
            onClick={() => setIsSettingsPanelOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: OCAColors.primary,
            }}
          >
            <MaterialIcon name="settings" size={24} color={OCAColors.primary} />
          </button>
        }
      >
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: Spacing.xl,
          color: OCAColors.secondaryLabel,
        }}>
          Loading device data...
        </div>
      ) : (
        <div style={{ padding: `0 0 ${Spacing.lg} 0` }}>
          {error && (
            <div style={{
              padding: Spacing.md,
              margin: Spacing.md,
              backgroundColor: OCAColors.danger + '15',
              borderRadius: '8px',
              color: OCAColors.danger,
              fontSize: Typography.fontSize.sm,
            }}>
              {error}
            </div>
          )}

          {/* Device Status Section */}
          <IOSListSection title="STATUS">
            <DeviceDetailListItem
              icon={<div style={{
                width: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: statusInfo.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MaterialIcon name="circle" size={20} color={OCAColors.background} />
              </div>}
              title="Device Status"
              value={statusInfo.status}
              statusColor={statusInfo.color}
              showChevron={false}
            />
            {device.lastSeen && (
              <DeviceDetailListItem
                icon={<div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  backgroundColor: OCAColors.gray500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MaterialIcon name="schedule" size={20} color={OCAColors.background} />
                </div>}
                title="Last Seen"
                value={new Date(device.lastSeen).toLocaleString()}
                showChevron={false}
              />
            )}
          </IOSListSection>

          {/* Action Buttons - Only show if there's an alarm */}
          {latestData && (isAlarmActive(latestData.dp0_alarm_state) || isAlarmActive(latestData.dp1_alarm_state)) && (
            <div style={{
              padding: `${Spacing.lg} ${Spacing.lg}`,
              backgroundColor: OCAColors.backgroundSecondary,
              borderTop: `0.5px solid ${OCAColors.separator}`,
            }}>
              <div style={{
                display: 'flex',
                gap: Spacing.sm,
                justifyContent: 'center',
              }}>
                <button
                  onClick={async () => {
                    console.log('ACK action for device:', device.deviceId)
                    const token = await getAccessToken()
                    if (token) {
                      const success = await sendAckCommand(token, device.deviceId)
                      if (success) {
                        console.log('✅ ACK command sent successfully')
                      } else {
                        console.error('❌ Failed to send ACK command')
                      }
                    } else {
                      console.error('❌ No access token available')
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: `${Spacing.md} ${Spacing.sm}`,
                    background: 'rgba(0, 122, 255, 0.85)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    color: OCAColors.background,
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: DeviceDetailTypography.fontSize.base,
                    fontWeight: DeviceDetailTypography.fontWeight.medium,
                    fontFamily: DeviceDetailTypography.fontFamily,
                    cursor: 'pointer',
                    minHeight: '44px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                  }}
                >
                  ACK
                </button>
                <button
                  onClick={() => setResolveModalOpen(true)}
                  disabled={isResolving}
                  style={{
                    flex: 1,
                    padding: `${Spacing.md} ${Spacing.sm}`,
                    background: isResolving ? 'rgba(142, 142, 147, 0.85)' : 'rgba(52, 199, 89, 0.85)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    color: OCAColors.background,
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: DeviceDetailTypography.fontSize.base,
                    fontWeight: DeviceDetailTypography.fontWeight.medium,
                    fontFamily: DeviceDetailTypography.fontFamily,
                    cursor: isResolving ? 'not-allowed' : 'pointer',
                    minHeight: '44px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isResolving ? 'none' : '0 4px 12px rgba(52, 199, 89, 0.3)',
                    opacity: isResolving ? 0.6 : 1,
                  }}
                >
                  {isResolving ? '...' : 'RESOLVE'}
                </button>
                {/* Only show Escalate button if an escalation policy exists */}
                {hasEscalationPolicy && (
                  <button
                    onClick={async () => {
                      console.log('Escalate action for device:', device.deviceId)
                      const token = await getAccessToken()
                      if (token) {
                        const success = await sendEscalateCommand(token, device.deviceId)
                        if (success) {
                          console.log('✅ Escalate command sent successfully')
                        } else {
                          console.error('❌ Failed to send Escalate command')
                        }
                      } else {
                        console.error('❌ No access token available')
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: `${Spacing.md} ${Spacing.sm}`,
                      background: 'rgba(255, 59, 48, 0.85)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      color: OCAColors.background,
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: DeviceDetailTypography.fontSize.base,
                      fontWeight: DeviceDetailTypography.fontWeight.medium,
                      fontFamily: DeviceDetailTypography.fontFamily,
                      cursor: 'pointer',
                      minHeight: '44px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 12px rgba(255, 59, 48, 0.3)',
                    }}
                  >
                    Escalate
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Latest Readings */}
          {latestData && (
            <IOSListSection title="LATEST READINGS">
              {latestData.dp0 !== undefined && latestData.dp0 !== null && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `${Spacing.md} ${Spacing.lg}`,
                    backgroundColor: OCAColors.background,
                    borderBottom: `0.5px solid ${OCAColors.separator}`,
                    fontFamily: DeviceDetailTypography.fontFamily,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: Spacing.sm }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '20px',
                      backgroundColor: isAlarmActive(latestData.dp0_alarm_state) ? OCAColors.deviceAlarm :
                                     isWarningState(latestData.dp0_alarm_state) ? OCAColors.deviceWarning :
                                     OCAColors.deviceOnline,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: Spacing.md,
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: '12px', color: OCAColors.background }}>Z1</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: DeviceDetailTypography.fontSize.base,
                        fontWeight: DeviceDetailTypography.fontWeight.normal,
                        color: OCAColors.label,
                        lineHeight: '18px',
                      }}>
                        Zone 1 Pressure
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      marginLeft: Spacing.sm,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: DeviceDetailTypography.fontSize.base, fontWeight: DeviceDetailTypography.fontWeight.medium }}>
                        {`${formatPressureValue(Number(latestData.dp0), pressureUnit)} ${pressureUnit}`}
                        {pressureTrend?.dp0 && (
                          <StackedChevrons
                            direction={pressureTrend?.dp0?.direction || 'up'}
                            count={pressureTrend?.dp0?.count as 1 | 2 | 3 || 1}
                            color={pressureTrend?.dp0?.direction === 'up' ? OCAColors.danger : OCAColors.success}
                            size={14}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                  {/* Sparkline Chart */}
                  {deviceData.length > 1 && (
                    <div style={{ marginLeft: '56px', marginTop: Spacing.xs }}>
                      <PressureSparkline
                        data={deviceData.slice(0, 24).reverse().map(d => ({
                          timestamp: new Date(d.timestamp).getTime(),
                          value: Number(d.dp0),
                          alarm_state: d.dp0_alarm_state,
                        }))}
                        width={200}
                        height={50}
                        warningThreshold={latestData.dp0wt ? Number(latestData.dp0wt) : undefined}
                        alarmThreshold={latestData.dp0at ? Number(latestData.dp0at) : undefined}
                        showThresholds={true}
                      />
                      <div style={{
                        fontSize: DeviceDetailTypography.fontSize.xs,
                        color: OCAColors.tertiaryLabel,
                        marginTop: '4px',
                      }}>
                        Last 24 hours
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* DP0 Thresholds */}
              {latestData.dp0wt !== undefined && latestData.dp0wt !== null && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.deviceWarning,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="warning" size={16} color={OCAColors.background} />
                  </div>}
                  title="Zone 1 Warning Threshold"
                  value={`${formatPressureValue(Number(latestData.dp0wt), pressureUnit)} ${pressureUnit}`}
                  showChevron={false}
                />
              )}
              {latestData.dp0at !== undefined && latestData.dp0at !== null && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.deviceAlarm,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="error" size={16} color={OCAColors.background} />
                  </div>}
                  title="Zone 1 Alarm Threshold"
                  value={`${formatPressureValue(Number(latestData.dp0at), pressureUnit)} ${pressureUnit}`}
                  showChevron={false}
                />
              )}
              {latestData.dp1 !== undefined && latestData.dp1 !== null && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `${Spacing.md} ${Spacing.lg}`,
                    backgroundColor: OCAColors.background,
                    borderBottom: `0.5px solid ${OCAColors.separator}`,
                    fontFamily: DeviceDetailTypography.fontFamily,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: Spacing.sm }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '20px',
                      backgroundColor: isAlarmActive(latestData.dp1_alarm_state) ? OCAColors.deviceAlarm :
                                     isWarningState(latestData.dp1_alarm_state) ? OCAColors.deviceWarning :
                                     OCAColors.deviceOnline,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: Spacing.md,
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: '12px', color: OCAColors.background }}>Z2</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: DeviceDetailTypography.fontSize.base,
                        fontWeight: DeviceDetailTypography.fontWeight.normal,
                        color: OCAColors.label,
                        lineHeight: '18px',
                      }}>
                        Zone 2 Pressure
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      marginLeft: Spacing.sm,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: DeviceDetailTypography.fontSize.base, fontWeight: DeviceDetailTypography.fontWeight.medium }}>
                        {`${formatPressureValue(Number(latestData.dp1), pressureUnit)} ${pressureUnit}`}
                        {pressureTrend?.dp1 && (
                          <StackedChevrons
                            direction={pressureTrend?.dp1?.direction || 'down'}
                            count={pressureTrend?.dp1?.count as 1 | 2 | 3 || 1}
                            color={pressureTrend?.dp1?.direction === 'up' ? OCAColors.danger : OCAColors.success}
                            size={14}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                  {/* Sparkline Chart */}
                  {deviceData.length > 1 && (
                    <div style={{ marginLeft: '56px', marginTop: Spacing.xs }}>
                      <PressureSparkline
                        data={deviceData.slice(0, 24).reverse().map(d => ({
                          timestamp: new Date(d.timestamp).getTime(),
                          value: Number(d.dp1),
                          alarm_state: d.dp1_alarm_state,
                        }))}
                        width={200}
                        height={50}
                        warningThreshold={latestData.dp1wt ? Number(latestData.dp1wt) : undefined}
                        alarmThreshold={latestData.dp1at ? Number(latestData.dp1at) : undefined}
                        showThresholds={true}
                      />
                      <div style={{
                        fontSize: DeviceDetailTypography.fontSize.xs,
                        color: OCAColors.tertiaryLabel,
                        marginTop: '4px',
                      }}>
                        Last 24 hours
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* DP1 Thresholds */}
              {latestData.dp1wt !== undefined && latestData.dp1wt !== null && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.deviceWarning,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="warning" size={16} color={OCAColors.background} />
                  </div>}
                  title="Zone 2 Warning Threshold"
                  value={`${formatPressureValue(Number(latestData.dp1wt), pressureUnit)} ${pressureUnit}`}
                  showChevron={false}
                />
              )}
              {latestData.dp1at !== undefined && latestData.dp1at !== null && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.deviceAlarm,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="error" size={16} color={OCAColors.background} />
                  </div>}
                  title="Zone 2 Alarm Threshold"
                  value={`${formatPressureValue(Number(latestData.dp1at), pressureUnit)} ${pressureUnit}`}
                  showChevron={false}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${Spacing.md} ${Spacing.lg}`,
                  backgroundColor: OCAColors.background,
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                  fontFamily: DeviceDetailTypography.fontFamily,
                  minHeight: '60px',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  backgroundColor: OCAColors.gray500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: Spacing.md,
                  flexShrink: 0,
                }}>
                  <MaterialIcon name="schedule" size={20} color={OCAColors.background} />
                </div>

                {/* Content */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}>
                  <div style={{
                    fontSize: DeviceDetailTypography.fontSize.base,
                    fontWeight: DeviceDetailTypography.fontWeight.normal,
                    color: OCAColors.label,
                    lineHeight: '18px',
                    marginBottom: '2px',
                  }}>
                    Reading Time
                  </div>
                </div>

                {/* Value - Right aligned */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  marginLeft: Spacing.sm,
                  flexShrink: 0,
                  maxWidth: '50%',
                }}>
                  <div style={{
                    fontSize: DeviceDetailTypography.fontSize.base,
                    fontWeight: DeviceDetailTypography.fontWeight.normal,
                    color: OCAColors.label,
                    lineHeight: '18px',
                    marginBottom: '2px',
                    textAlign: 'right',
                  }}>
                    {new Date(latestData.timestamp).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                  <div style={{
                    fontSize: DeviceDetailTypography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                    lineHeight: '16px',
                    textAlign: 'right',
                  }}>
                    {new Date(latestData.timestamp).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>
              </div>
            </IOSListSection>
          )}

          {/* Connection Status */}
          {latestData && (
            <IOSListSection title="CONNECTION">
              {latestData.w_rssi !== undefined && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="wifi" size={20} color={OCAColors.background} />
                  </div>}
                  title="WiFi Signal"
                  value={`${latestData.w_rssi} dBm`}
                  showChevron={false}
                />
              )}
              {latestData.c_rssi !== undefined && (
                <DeviceDetailListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.warning,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="signal_cellular_alt" size={20} color={OCAColors.background} />
                  </div>}
                  title="Cellular Signal"
                  value={`${latestData.c_rssi} dBm`}
                  showChevron={false}
                />
              )}
            </IOSListSection>
          )}

          {/* Location Information */}
          {device.locationId && (
            <IOSListSection title="LOCATION">
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: Spacing.xs,
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.gray500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: Spacing.md,
                    flexShrink: 0,
                  }}>
                    <MaterialIcon name="location_on" size={20} color={OCAColors.background} />
                  </div>
                  <div style={{
                    fontSize: DeviceDetailTypography.fontSize.base,
                    fontWeight: DeviceDetailTypography.fontWeight.normal,
                    color: OCAColors.label,
                    fontFamily: DeviceDetailTypography.fontFamily,
                  }}>
                    Location
                  </div>
                </div>
                <div style={{
                  fontSize: DeviceDetailTypography.fontSize.sm,
                  color: OCAColors.label,
                  fontFamily: DeviceDetailTypography.fontFamily,
                  fontWeight: DeviceDetailTypography.fontWeight.normal,
                  lineHeight: '18px',
                  marginLeft: '56px',
                  wordBreak: 'break-word',
                  marginTop: '2px',
                }}>
                  {locationName}
                </div>
              </div>
            </IOSListSection>
          )}
        </div>
      )}
    </IOSSlideUpPanel>

      {/* Device Settings Panel */}
      {isSettingsPanelOpen && device && (
        <DeviceSettingsPanel
          device={device}
          isOpen={isSettingsPanelOpen}
          onClose={() => setIsSettingsPanelOpen(false)}
        />
      )}

      {/* Resolve Modal */}
      <AlarmResolveModal
        isOpen={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false)
          setError('')
        }}
        escalationId={`device-${device?.deviceId || 'unknown'}`}
        deviceName={deviceName}
        onSubmit={handleResolveSubmit}
      />
    </>
  )
}